// path: frontend/src/hooks/useDataHelpers.js
/**
 * UI-хелперы для справочников (имена, отделы, отпуска). Не содержит
 * бизнес-логики: empName/primaryDept — маппинг id → имя/отдел;
 * vacOverlap — поиск пересечения с отпуском для UX-предупреждения.
 * getTaskSpent — суммарные часы задачи (приоритет task.actualHours).
 */
import { useCallback, useMemo } from "react";
import { useEmployeesQuery } from "../api/queries/employees";
import { useDepartmentsQuery } from "../api/queries/departments";
import { useVacationsQuery } from "../api/queries/vacations";

export const useDataHelpers = (data) => {
  const { data: employeesFromQuery = [] } = useEmployeesQuery();
  const { data: departmentsFromQuery = [] } = useDepartmentsQuery();
  const { data: vacationsFromQuery = [] } = useVacationsQuery();

  const employees = data?.employees ?? employeesFromQuery;
  const departments = data?.departments ?? departmentsFromQuery;
  const vacations = data?.vacations ?? vacationsFromQuery;

  const employeesById = useMemo(
    () => new Map(employees.map((e) => [e.id, e])),
    [employees],
  );
  const departmentsById = useMemo(
    () => new Map(departments.map((d) => [d.id, d])),
    [departments],
  );

  const empName = useCallback(
    (id) => {
      const e = employeesById.get(id);
      return e ? `${e.last} ${e.first}` : "-";
    },
    [employeesById],
  );

  const primaryDept = useCallback(
    (emp) => {
      if (!emp) return null;
      const p = emp.departments?.find((x) => x.primary) || emp.departments?.[0];
      return p ? departmentsById.get(p.deptId) || null : null;
    },
    [departmentsById],
  );

  const getTaskSpent = useCallback((task) => {
    if (!task) return 0;
    if (
      typeof task.actualHours === "number" &&
      Number.isFinite(task.actualHours)
    ) {
      return task.actualHours;
    }
    if (Array.isArray(task.logs)) {
      return task.logs.reduce((sum, l) => sum + (Number(l.hours) || 0), 0);
    }
    return 0;
  }, []);

  const vacOverlap = useCallback(
    (empId, from, to) => {
      if (!from || !to) return null;
      return (
        vacations.find(
          (v) =>
            v.empId === empId &&
            v.status === "approved" &&
            v.start <= to &&
            v.end >= from,
        ) || null
      );
    },
    [vacations],
  );

  return {
    employees,
    departments,
    empName,
    primaryDept,
    getTaskSpent,
    vacOverlap,
  };
};
